import { useCallback, useEffect, useRef } from 'react';
import { conversationSuggestionsService } from '../services/conversationSuggestionsService';
import type {
  ConversationSuggestion,
  ConversationSuggestionsResponse,
} from '../types/conversationSuggestions';
import type { SupportedUiLocale } from '../localization/locales';
import { logger } from '../utils/logger';

const moduleLogger = logger.getModule('useConversationSuggestions');

const isAbortError = (error: unknown): boolean => (
  error instanceof DOMException && error.name === 'AbortError'
);

interface UseConversationSuggestionsOptions {
  sessionId: string | undefined;
  responseLocale: SupportedUiLocale;
}

interface UseConversationSuggestionsResult {
  requestSuggestions: (
    sourceTurnNumber: number,
    onReady?: (suggestions: ConversationSuggestion[]) => void,
    onUnavailable?: () => void,
  ) => void;
}

export const useConversationSuggestions = ({
  sessionId,
  responseLocale,
}: UseConversationSuggestionsOptions): UseConversationSuggestionsResult => {
  const activeRequestControllersRef = useRef(new Set<AbortController>());
  const requestEpochRef = useRef(0);

  const abortRequests = useCallback(() => {
    requestEpochRef.current += 1;
    activeRequestControllersRef.current.forEach((controller) => controller.abort());
    activeRequestControllersRef.current.clear();
  }, []);

  useEffect(() => {
    abortRequests();
    return abortRequests;
  }, [abortRequests, responseLocale, sessionId]);

  const requestSuggestions = useCallback((
    sourceTurnNumber: number,
    onReady?: (suggestions: ConversationSuggestion[]) => void,
    onUnavailable?: () => void,
  ) => {
    if (!sessionId) {
      return;
    }

    const requestEpoch = requestEpochRef.current;
    const requestController = new AbortController();
    activeRequestControllersRef.current.add(requestController);

    void conversationSuggestionsService.generateSuggestions(
      sessionId,
      {
        source_turn_number: sourceTurnNumber,
        response_locale: responseLocale,
      },
      { signal: requestController.signal },
    ).then((response: ConversationSuggestionsResponse) => {
      if (
        requestEpoch !== requestEpochRef.current
        || response.source_turn_number !== sourceTurnNumber
      ) {
        onUnavailable?.();
        return;
      }

      const generatedSuggestions = response.status === 'ready' ? response.suggestions : [];
      onReady?.(generatedSuggestions);
    }).catch((error: unknown) => {
      if (requestEpoch === requestEpochRef.current && !isAbortError(error)) {
        moduleLogger.warn('Generated conversation suggestions are unavailable', {
          sourceTurnNumber,
        });
        onUnavailable?.();
      }
    }).finally(() => {
      activeRequestControllersRef.current.delete(requestController);
    });
  }, [responseLocale, sessionId]);

  return {
    requestSuggestions,
  };
};
